import { useContext, useEffect, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import MailOutlined from '@mui/icons-material/MailOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import SsoService from '../../../shared/services/sso/sso.service';
import { ICheckTokenResponse } from '../../../shared/services/user-auth/user-auth.model';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { TermsAndConditionsButton } from '../../presentation-components/signup-login/terms-and-conditions-button';
import { invitedOrientationStorageKey } from '../../presentation-components/dashboard-components/invited-user-orientation-banner';
import { AuthLayout } from './auth/auth-layout';
import { AuthField } from './auth/auth-field';
import { PasswordAdornment } from './auth/password-adornment';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

export const SignupInvitedUserPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userAuthService = new UserAuthService();
  const ssoService = new SsoService();
  const userDetailsContext = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const branding = useAuthBranding();

  const [token, setToken] = useState<string>();
  const [tokenDetails, setTokenDetails] = useState<ICheckTokenResponse>();
  const [tokenChecked, setTokenChecked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // SSO-enforced orgs accept the invite through the identity provider instead of
  // setting a password.
  const [ssoTerms, setSsoTerms] = useState(false);
  const [ssoTermsError, setSsoTermsError] = useState(false);
  const [ssoSubmitting, setSsoSubmitting] = useState(false);

  const handleSsoAccept = async () => {
    if (!ssoTerms) { setSsoTermsError(true); return; }
    setSsoTermsError(false);
    setErrorMessage('');
    setSsoSubmitting(true);
    try {
      await userAuthService.acceptInviteWithSso({ token, termsAndConditions: true });
      const result = await ssoService.getAuthMethod(tokenDetails?.email || '', '/dashboard');
      if (result.method === 'sso' && result.ssoStartUrl) {
        window.location.href = result.ssoStartUrl;
        return;
      }
      setErrorMessage('Single sign-on is not available for your organisation yet. Please contact your administrator.');
      setSsoSubmitting(false);
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.message || 'There was an error, please try again.');
      setSsoSubmitting(false);
    }
  };

  const signUpSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    surname: Yup.string().required('Last name is required'),
    email: Yup.string().email('Email is invalid').required('Email is required'),
    password: Yup.string().min(8, 'Your password must be at least 8 characters').required('Password is required'),
    passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('You must confirm your password'),
    termsAndConditions: Yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      surname: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      termsAndConditions: false,
    },
    validationSchema: signUpSchema,
    onSubmit: (values) => {
      setErrorMessage('');
      return userAuthService
        .completeUserSignUpFromToken({ ...values, token })
        .then(() => {
          userDetailsContext.forceUpdateUserDetails().then((userDetails) => {
            // This page is the only place that knows the signup came from an
            // invite (the session payload carries no invite fields), so flag it
            // here for the dashboard's one-time orientation banner.
            if (userDetails && !userDetails.isPartner) {
              localStorage.setItem(invitedOrientationStorageKey(userDetails.id), 'true');
            }
            navigate(userDetails?.isPartner ? '/partner-dashboard' : '/dashboard', userDetails?.isPartner ? undefined : { state: { justAuthenticated: true } });
          });
        })
        .catch((err) => {
          console.log(err);
          if (err.response?.data?.message) {
            setErrorMessage(err.response.data.message);
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
        });
    },
  });

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setTokenChecked(true);
      setErrorMessage('This invitation link is missing its token. Please use the link from your invitation email.');
      return;
    }
    setToken(t);
    userAuthService
      .checkUserToken(t)
      .then((response: { details: ICheckTokenResponse }) => {
        setTokenDetails(response.details);
        formik.setValues({ ...formik.values, ...response.details });
        setTokenChecked(true);
      })
      .catch((err) => {
        console.log(err);
        setTokenChecked(true);
        setErrorMessage(err.response?.data?.message || 'This invitation link is invalid or has expired.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inviter = tokenDetails?.partnerName || branding.productName;

  // Loading / invalid-token states.
  if (!tokenChecked) {
    return (
      <AuthLayout branding={branding}>
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 200 }}>
          <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          <Typography sx={{ fontSize: '0.9375rem', color: AUTH_TOKENS.inkSecondary }}>Checking your invitation…</Typography>
        </Stack>
      </AuthLayout>
    );
  }

  if (!tokenDetails) {
    return (
      <AuthLayout branding={branding}>
        <Typography component="h1" sx={{ mb: '10px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em' }}>
          Invitation unavailable
        </Typography>
        <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          {errorMessage}
        </Typography>
      </AuthLayout>
    );
  }

  // SSO-enforced org: no password, accept terms and continue through the IdP.
  if (tokenDetails.enforceSso) {
    return (
      <AuthLayout branding={branding}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Join {tokenDetails.organisationName || inviter}
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          You&apos;ve been invited to {inviter}, which uses single sign-on. Accept the terms below and continue with your organisation account.
        </Typography>

        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: '-3px' }}
          control={
            <Checkbox
              checked={ssoTerms}
              onChange={(e) => { setSsoTerms(e.target.checked); if (e.target.checked) setSsoTermsError(false); }}
              disableRipple
              sx={{ py: 0, pr: 1 }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary, lineHeight: 1.5 }}>
              I accept the
              <TermsAndConditionsButton color={branding.secrInk} />
            </Typography>
          }
        />
        {ssoTermsError && (
          <FormHelperText error sx={{ ml: '2px', fontSize: '0.8125rem' }}>
            You must agree to the terms and conditions
          </FormHelperText>
        )}
        {errorMessage && (
          <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
            {errorMessage}
          </FormHelperText>
        )}

        <Button
          fullWidth
          size="large"
          disabled={ssoSubmitting}
          onClick={handleSsoAccept}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {ssoSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Accept and continue with single sign-on'}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Join {tokenDetails.organisationName || inviter}
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          You&apos;ve been invited to {inviter}. Set up your account to get started.
        </Typography>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <AuthField
              id="firstName"
              name="firstName"
              label="First name"
              placeholder="First name"
              autoComplete="given-name"
              icon={<PersonOutlined />}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName ? formik.errors.firstName : undefined}
              focusRing={branding.focusRing}
            />
            <AuthField
              id="surname"
              name="surname"
              label="Last name"
              placeholder="Last name"
              autoComplete="family-name"
              icon={<PersonOutlined />}
              value={formik.values.surname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.surname && Boolean(formik.errors.surname)}
              helperText={formik.touched.surname ? formik.errors.surname : undefined}
              focusRing={branding.focusRing}
            />
          </Stack>

          <AuthField
            id="email"
            name="email"
            label="Work email"
            type="email"
            icon={<MailOutlined />}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            focusRing={branding.focusRing}
            disabled
          />

          <AuthField
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password ? formik.errors.password : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
          />

          <AuthField
            id="passwordConfirmation"
            name="passwordConfirmation"
            label="Confirm password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
            helperText={formik.touched.passwordConfirmation ? formik.errors.passwordConfirmation : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />}
          />
        </Stack>

        <FormControlLabel
          sx={{ mt: '16px', alignItems: 'flex-start', ml: '-3px' }}
          control={
            <Checkbox
              id="termsAndConditions"
              name="termsAndConditions"
              checked={formik.values.termsAndConditions}
              onChange={formik.handleChange}
              disableRipple
              sx={{ py: 0, pr: 1 }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary, lineHeight: 1.5 }}>
              I accept the
              <TermsAndConditionsButton color={branding.secrInk} />
            </Typography>
          }
        />
        {formik.touched.termsAndConditions && formik.errors.termsAndConditions && (
          <FormHelperText error sx={{ ml: '2px', fontSize: '0.8125rem' }}>
            {formik.errors.termsAndConditions}
          </FormHelperText>
        )}

        {errorMessage && (
          <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
            {errorMessage}
          </FormHelperText>
        )}

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={formik.isSubmitting}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Create account'}
        </Button>
      </Box>
    </AuthLayout>
  );
};
